import Tooltip from 'components/@extended/Tooltip';
import { buttonStyle } from 'components/buttons/buttonStyle';
import CancelButton from 'components/buttons/CancelButton';
import PermissionButton from 'components/buttons/PermissionButton';
import SaveButton from 'components/buttons/SaveButton';
import {
    useCreateDialogRequest,
    useCreateDialogValues,
    useCreateRoute,
    useOpenCreateUrl,
} from 'components/create-dialog/CreateDialogContext';
import { CreateDialogActionBar } from 'components/create-dialog/CreateDialogFooter';
import { type DraftLeave, draftIdIn, useCreateDraft } from 'components/create-dialog/useCreateDraft';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftNotice } from 'components/drafts/DraftNotice';
import { DraftMeta, DraftRecordActions } from 'components/drafts/DraftRecordActions';
import { DraftSaveMode, useDraftField } from 'components/drafts/DraftSaveMode';
import { DraftSharedWith } from 'components/drafts/DraftSharedWith';
import { DraftActionsContext, useDraftActions } from 'components/drafts/useDraftActions';
import { MoreVertOutlined as MoreVertOutlinedIcon } from 'components/icons';
import Loader from 'components/Loader';
import MarketingIntegrationLink from 'components/marketingintegrationlink/MarketingIntegrationLink';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { DraftLeaveModal } from 'components/navigation/ConfirmNavigation';
import KebabMenu, { MenuItem as KebabMenuItem } from 'components/navigation/KebabMenu';
import PageHeader from 'components/PageHeader';
import { Button } from 'components/ui/button';
import { Tooltip as KitTooltip } from 'components/ui/tooltip';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import capitalize from 'lodash/capitalize';
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormHandleSubmit } from 'react-hook-form';
import { useBlocker, useNavigate } from 'react-router-dom';
import { dispatch } from 'store';
import { useStartMarketingProfileExportMutation } from 'store/api/exports';
import {
    useCreateMarketingProfileMutation,
    useDeleteMarketingProfileMutation,
    useGetMarketingProfileByIdQuery,
    useUpdateMarketingProfileMutation,
} from 'store/api/marketingProfiles';
import { addExport } from 'store/reducers/exports';
import { MarketingProfileCreateUpdateRequest, MarketingProfileFilterCriteria } from 'types/marketingProfiles';
import { errorAlert, successAlert } from 'utils/alert';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import { getIntl, t } from 'utils/getTranslations';
import { setLocalStorage } from 'utils/localStorage';
import { isNewDesignCopy } from 'utils/newDesignCopy';
import { useHasPermissions } from 'utils/permissionUtil';
import { sanitizeFileName } from 'utils/text';
import { useLeaveDeletedEntity } from 'utils/useNavigateWithReturnState';
import { MarketingProfileBody } from './MarketingProfileBody';
import { buildMarketingProfileRequest, normalizeFilterCriteria } from './marketingProfileFormUtils';

const DELETE_PERMISSIONS = ['DELETE.MARKETING_PROFILE'];
const EXPORT_PERMISSIONS = ['READ.MARKETING.ALL'];
const CREATE_PERMISSIONS = ['CREATE.MARKETING_PROFILE'];
const UPDATE_PERMISSIONS = ['UPDATE.MARKETING_PROFILE'];
const EXPORT_SIZE_LIMIT = 10_000;
const EXPORT_DISABLED_REASON_ID = 'marketing-profile-export-disabled-reason';

type EditorState = {
    filterCriteria: MarketingProfileFilterCriteria;
    isDirty: boolean;
    hasCriteria: boolean;
    count?: number;
};

type Props = {
    initialValue?: MarketingProfileCreateUpdateRequest;
    forceCreateMode?: boolean;
};

function buildExportFileName(profileName: string): string {
    const safeName = sanitizeFileName(profileName);
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '_');
    return `${safeName}_${date}.xlsx`;
}

type LeaveGuardProps = {
    isDirty: boolean;
    isCreateMode: boolean;
    /** Set around a navigation of the page's own, which the guard lets through. */
    allowNavRef: RefObject<boolean>;
    onSave: () => Promise<boolean>;
    /** A new profile's way to be kept as a draft, which the question then offers in place of saving it. */
    draft: DraftLeave | null;
};

/**
 * Asks before the profile's page is left with unsaved changes. A component of its own, so that the create dialog,
 * which is on no route and may be open over a page with a blocker of its own, mounts none.
 */
function LeaveGuard({ isDirty, isCreateMode, allowNavRef, onSave, draft }: LeaveGuardProps) {
    const { openModal, closeModal } = useModal();
    // Save as draft leaving for the draft's page leaves nothing unsaved.
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            isDirty &&
            !allowNavRef.current &&
            !draft?.leaving.current &&
            currentLocation.pathname !== nextLocation.pathname
    );

    useEffect(() => {
        if (blocker.state !== 'blocked') return;
        const cancelNavigation = () => {
            blocker.reset();
            closeModal();
        };
        const revertAndProceed = () => {
            closeModal();
            allowNavRef.current = true;
            blocker.proceed();
        };
        const saveAndProceed = async () => {
            closeModal();
            const saved = await onSave();
            if (saved) {
                allowNavRef.current = true;
                blocker.proceed();
            } else {
                blocker.reset();
            }
        };
        if (draft) {
            openModal(
                <DraftLeaveModal
                    draft={draft}
                    onBack={cancelNavigation}
                    onLeave={revertAndProceed}
                    onSaved={revertAndProceed}
                />,
                cancelNavigation
            );
            return;
        }
        openModal(
            <ConfirmationModal
                onCancel={cancelNavigation}
                onConfirm={saveAndProceed}
                confirmId="save"
                content={<p className="text-body1">{t('marketing-profiles.unsaved-changes-close-hint')}</p>}
                extraButton={
                    <Button {...buttonStyle({ variant: 'outlined', color: 'error' })} onClick={revertAndProceed}>
                        {t(isCreateMode ? 'discard' : 'revert')}
                    </Button>
                }
            />,
            cancelNavigation,
            { maxWidth: '433px' }
        );
    }, [blocker.state]); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
}

/** Tells a draft save which of the two parts a profile is saved with it lacks. */
function MarketingProfileDraftFields({ name, hasCriteria }: { name: string; hasCriteria: boolean }) {
    useDraftField(t('marketing-profiles.profile-name'), true, name.trim() === '');
    useDraftField(t('marketing-profiles.details.drawerTitle'), true, !hasCriteria);
    return null;
}

const MarketingProfileDetailsPage = ({ initialValue: initialPrefill, forceCreateMode = false }: Props) => {
    // In the create dialog the router describes the page behind it, whose id is another profile's.
    const { id } = useCreateRoute().params;
    const openCreateUrl = useOpenCreateUrl();
    const createDialogRequest = useCreateDialogRequest();
    const navigate = useNavigate();
    const leaveDeleted = useLeaveDeletedEntity('/contacts/marketing');
    const isCreateMode = forceCreateMode || !id || id === 'new';
    const numericId = isCreateMode ? undefined : Number(id);

    const { openModal, closeModal } = useModal();
    const hasCreatePermission = useHasPermissions(CREATE_PERMISSIONS);
    const hasDeletePermission = useHasPermissions(DELETE_PERMISSIONS);
    const [isDeletingAndLeaving, setIsDeletingAndLeaving] = useState(false);
    const shouldSkipProfileQuery = isCreateMode || Number.isNaN(numericId) || isDeletingAndLeaving;

    const {
        data: profile,
        isLoading: isLoadingProfile,
        error: profileError,
    } = useGetMarketingProfileByIdQuery(numericId!, {
        skip: shouldSkipProfileQuery,
        // Read afresh: a draft completed in its form opens here, where the form's read of it may still be cached.
        refetchOnMountOrArgChange: true,
    });
    const isDraft = !isCreateMode && !!profile?.isDraft;
    const draftActions = useDraftActions({
        draft: isDraft && profile ? { type: 'marketing-profile', id: profile.id } : null,
        createRoute: '/contacts/marketing/new',
        onDeleted: leaveDeleted,
    });

    const [createProfile, { isLoading: isCreating }] = useCreateMarketingProfileMutation();
    const [updateProfile, { isLoading: isUpdating }] = useUpdateMarketingProfileMutation();
    const [deleteProfile, { isLoading: isDeleting }] = useDeleteMarketingProfileMutation();
    const [startExport, { isLoading: isExporting }] = useStartMarketingProfileExportMutation();

    const [profileName, setProfileName] = useState(isCreateMode ? (initialPrefill?.name ?? '') : (profile?.name ?? ''));
    const [editorState, setEditorState] = useState<EditorState | null>(null);
    const handleEditorStateChange = useCallback((next: Partial<EditorState> | null) => {
        setEditorState((prev) => {
            if (next === null) return null;
            return { ...(prev ?? { filterCriteria: {}, isDirty: false, hasCriteria: false }), ...next };
        });
    }, []);
    const [formError, setFormError] = useState<string | null>(null);
    const allowNavRef = useRef(false);
    const saveInFlightRef = useRef(false);
    const exportInFlightRef = useRef(false);

    useEffect(() => {
        setProfileName(isCreateMode ? (initialPrefill?.name ?? '') : (profile?.name ?? ''));
    }, [initialPrefill?.name, isCreateMode, profile?.name]);

    const isSaving = isCreating || isUpdating;

    const initialValue = useMemo<MarketingProfileCreateUpdateRequest | undefined>(
        () =>
            isCreateMode && initialPrefill
                ? initialPrefill
                : profile
                  ? { name: profile.name ?? '', filterCriteria: profile.filterCriteria }
                  : undefined,
        [initialPrefill, isCreateMode, profile]
    );

    // What the create dialog reads of a profile under way, to ask about it before it closes: the name and the
    // criteria the table last applied, which is what a profile is saved with.
    const dialogValues = useMemo(
        () => ({
            name: profileName,
            filterCriteria: normalizeFilterCriteria(editorState?.filterCriteria ?? initialValue?.filterCriteria ?? {}),
        }),
        [profileName, editorState?.filterCriteria, initialValue?.filterCriteria]
    );
    useCreateDialogValues(dialogValues);
    // No form library here: a draft save takes the request a save would send, which carries what was typed.
    const draftForm = useMemo(() => {
        const handleSubmit: UseFormHandleSubmit<MarketingProfileCreateUpdateRequest> = (onValid) => async () => {
            await onValid(buildMarketingProfileRequest(dialogValues.name, dialogValues.filterCriteria));
        };
        // Filled by the user: a name or criteria other than those the page opened with.
        const isDirty = (editorState?.isDirty ?? false) || profileName !== (initialValue?.name ?? '');
        return { handleSubmit, formState: { isDirty } };
    }, [dialogValues, editorState?.isDirty, profileName, initialValue?.name]);
    const draft = useCreateDraft({
        record: 'marketing-profile',
        methods: draftForm,
        toPayload: (request: MarketingProfileCreateUpdateRequest) => request,
    });

    // A draft's form starts from what the draft stored, which leaving it unchanged loses nothing of.
    const savedName = isCreateMode && draft.draftId !== null ? (initialPrefill?.name ?? '') : (profile?.name ?? '');
    const isDirty = (editorState?.isDirty ?? false) || profileName !== savedName;
    const hasCriteria =
        editorState?.hasCriteria ??
        (initialValue?.filterCriteria
            ? Object.values(initialValue.filterCriteria).some((v) => (Array.isArray(v) ? v.length > 0 : v === true))
            : false);
    const hasName = profileName.trim().length > 0;
    // A new profile is saved unchanged too: an unchanged draft is created as it is.
    const canSave = (isDirty || isCreateMode) && hasCriteria && hasName && !isSaving;
    const saveDisabledReason = !canSave && !isSaving ? 'marketing-profiles.save-requires-criteria' : undefined;

    const handleSaveInternal = async (): Promise<boolean> => {
        if (saveInFlightRef.current) return false;
        const filterCriteria = editorState?.filterCriteria ?? initialValue?.filterCriteria;
        const criteriaIsNonEmpty = filterCriteria
            ? Object.values(filterCriteria).some((v) => (Array.isArray(v) ? v.length > 0 : v === true))
            : false;
        if (!filterCriteria || !criteriaIsNonEmpty || !profileName.trim()) return false;
        const request = buildMarketingProfileRequest(profileName, filterCriteria);
        setFormError(null);
        saveInFlightRef.current = true;
        try {
            if (isCreateMode) {
                // A draft is created by completing it: the profile keeps the draft's id.
                const created =
                    draft.draftId === null ? await createProfile(request).unwrap() : await draft.complete(request);
                const createdUrl = `/contacts/marketing/${created.id}`;
                if (createDialogRequest) {
                    // The dialog stays on the page it was opened over and tells of the new profile itself.
                    createDialogRequest.finish(createdUrl);
                } else {
                    successAlert(t('marketing-profiles.profile-saved'));
                    allowNavRef.current = true;
                    navigate(createdUrl);
                    allowNavRef.current = false;
                }
            } else {
                await updateProfile({ id: numericId!, body: request }).unwrap();
                successAlert(t('marketing-profiles.profile-saved'));
                setEditorState((prev) => (prev ? { ...prev, isDirty: false } : null));
            }
            return true;
        } catch (error) {
            const message = getApiErrorMessage(error) ?? t('marketing-profiles.save-validation-error');
            setFormError(message);
            return false;
        } finally {
            saveInFlightRef.current = false;
        }
    };

    const handleCancel = () => {
        if (isCreateMode) {
            if (!isDirty) {
                allowNavRef.current = true;
                navigate('/contacts/marketing');
                return;
            }
            // The leave question then offers to keep it as a draft.
            if (draft.leave) {
                navigate('/contacts/marketing');
                return;
            }
            const discardAndLeave = () => {
                closeModal();
                allowNavRef.current = true;
                navigate('/contacts/marketing');
            };
            const saveAndLeave = async () => {
                closeModal();
                const saved = await handleSaveInternal();
                if (saved) {
                    allowNavRef.current = true;
                    navigate('/contacts/marketing');
                }
            };
            openModal(
                <ConfirmationModal
                    onCancel={closeModal}
                    onConfirm={() => {
                        void saveAndLeave();
                    }}
                    confirmId="save"
                    content={<p className="text-body1">{t('marketing-profiles.discard-new-profile-hint')}</p>}
                    extraButton={
                        <Button {...buttonStyle({ variant: 'outlined', color: 'error' })} onClick={discardAndLeave}>
                            {t('discard')}
                        </Button>
                    }
                />,
                closeModal,
                { maxWidth: '433px' }
            );
            return;
        }
        if (!isDirty) {
            allowNavRef.current = true;
        }
        navigate('/contacts/marketing');
    };

    const handleDelete = useCallback(() => {
        const confirmDelete = async () => {
            setIsDeletingAndLeaving(true);
            try {
                await deleteProfile(numericId!).unwrap();
                allowNavRef.current = true;
                successAlert(t('marketing-profiles.profile-deleted'));
                leaveDeleted();
            } catch {
                setIsDeletingAndLeaving(false);
                errorAlert(t('marketing-profiles.profile-delete-failed'));
            } finally {
                closeModal();
            }
        };

        openModal(
            <ConfirmationModal
                onCancel={closeModal}
                onConfirm={() => {
                    void confirmDelete();
                }}
                content={<p className="text-body1">{t('marketing-profiles.delete-confirmation')}</p>}
            />,
            closeModal,
            { maxWidth: '433px' }
        );
    }, [closeModal, deleteProfile, leaveDeleted, numericId, openModal]);

    const handleExport = async () => {
        if (!profile) return;
        if (exportInFlightRef.current) return;
        exportInFlightRef.current = true;
        try {
            const liveCriteria = editorState?.filterCriteria;
            const result = await startExport({ id: numericId!, criteria: liveCriteria }).unwrap();
            const fileName = buildExportFileName(profile.name ?? '');
            setLocalStorage(`export:filename:${result.taskId}`, fileName);
            dispatch(
                addExport({
                    taskId: result.taskId,
                    entityType: 'MARKETING_PROFILE',
                    status: result.status,
                    fileName,
                    startedAt: result.createdAt,
                })
            );
            successAlert(t('export-started', { entityName: capitalize(t('marketing-profiles.entity-name')) }));
        } catch {
            errorAlert(t('export-failed', { entityName: capitalize(t('marketing-profiles.entity-name')) }));
        } finally {
            exportInFlightRef.current = false;
        }
    };

    const profileActionMenuItems = useMemo<KebabMenuItem[]>(() => {
        if (isCreateMode || !numericId) return [];

        const items: KebabMenuItem[] = [];
        if (hasCreatePermission) {
            items.push({
                title: t('marketing-profiles.duplicate-profile-action'),
                // Where the create dialog exists the copy opens in it, over this profile.
                handler: () => openCreateUrl(`/contacts/marketing/duplicate/${numericId}`),
            });
        }
        if (hasDeletePermission && !isDeleting) {
            items.push({
                title: t('marketing-profiles.delete-profile-action'),
                handler: handleDelete,
                variant: 'destructive',
            });
        }
        return items;
    }, [openCreateUrl, handleDelete, hasCreatePermission, hasDeletePermission, isCreateMode, isDeleting, numericId]);

    // A draft is named as its form names it, or as a new profile while it has no name yet.
    const draftName = (isDraft ? profile?.name : draft.detail?.name) ?? null;
    const pageTitle =
        draftName || (isCreateMode || isDraft ? t('marketing-profiles.new-profile') : (profile?.name ?? ''));
    const effectiveCount = editorState?.count;
    const exceedsExportLimit = effectiveCount !== undefined && effectiveCount > EXPORT_SIZE_LIMIT;
    const cannotExport = effectiveCount === undefined || effectiveCount === 0 || exceedsExportLimit;
    const exportDisabledReason =
        isExporting || !cannotExport
            ? undefined
            : exceedsExportLimit
              ? t('marketing-profiles.export-exceeds-limit', {
                    limit: isNewDesignCopy()
                        ? getIntl().formatNumber(EXPORT_SIZE_LIMIT)
                        : EXPORT_SIZE_LIMIT.toLocaleString(),
                })
              : t('marketing-profiles.export-requires-contacts');
    // A disabled button takes no focus, so the wrapper does while a reason applies: that is what opens the
    // tooltip from the keyboard, and the hidden copy reaches screen readers, which the tooltip does not.
    const exportButton = (
        <span tabIndex={exportDisabledReason ? 0 : undefined}>
            <PermissionButton
                permissions={EXPORT_PERMISSIONS}
                variant="outlined"
                size="small"
                disabled={isExporting || cannotExport}
                aria-describedby={exportDisabledReason ? EXPORT_DISABLED_REASON_ID : undefined}
                onClick={() => {
                    void handleExport();
                }}
            >
                {t('export')}
            </PermissionButton>
            {exportDisabledReason ? (
                <span id={EXPORT_DISABLED_REASON_ID} className="sr-only">
                    {exportDisabledReason}
                </span>
            ) : null}
        </span>
    );

    const body = (
        <MarketingProfileBody
            name={profileName}
            onNameChange={setProfileName}
            error={formError}
            // A draft's page saves nothing: its name is changed in its form, through Edit.
            isSaving={isSaving || isDraft}
            showContacts={isCreateMode || (!isLoadingProfile && !profileError)}
            hasCriteria={hasCriteria}
            initialValue={initialValue}
            profileId={numericId}
            onStateChange={handleEditorStateChange}
        />
    );

    // A new profile's body, which a draft save reads what it lacks from.
    const createBody = (
        <DraftSaveMode mode={draft.mode}>
            <MarketingProfileDraftFields name={profileName} hasCriteria={hasCriteria} />
            {body}
        </DraftSaveMode>
    );

    if (createDialogRequest) {
        return (
            <>
                <CreateDialogActionBar
                    actions={[
                        <CancelButton key="cancel" onClick={createDialogRequest.close} />,
                        ...(draft.button ? [draft.button] : []),
                        <SaveButton
                            key="save"
                            permissions={CREATE_PERMISSIONS}
                            disabled={!canSave}
                            tooltip={saveDisabledReason}
                            onClick={() => {
                                void handleSaveInternal();
                            }}
                        />,
                    ]}
                />
                {createBody}
            </>
        );
    }

    const draftNotice = draftActions.draft ? <DraftNotice actions={draftActions} /> : null;

    return (
        <PaddedLayout className="overflow-hidden">
            <div className="flex h-full flex-col gap-4">
                <LeaveGuard
                    isDirty={isDirty && !isDraft}
                    isCreateMode={isCreateMode}
                    allowNavRef={allowNavRef}
                    onSave={handleSaveInternal}
                    draft={isCreateMode ? draft.leave : null}
                />
                <PageHeader
                    title={pageTitle}
                    showBreadcrumb
                    onBack={handleCancel}
                    iconsEnd={
                        !isCreateMode && !isLoadingProfile && profile?.marketingIntegrationLink ? (
                            <MarketingIntegrationLink
                                href={profile.marketingIntegrationLink}
                                integrationName="Smaily"
                            />
                        ) : undefined
                    }
                    buttons={
                        isDraft ? (
                            <div className="flex items-center justify-end gap-2">
                                <DraftRecordActions actions={draftActions} />
                            </div>
                        ) : (
                            <div className="flex gap-2">
                                {profileActionMenuItems.length > 0 && (
                                    <KebabMenu
                                        menuItems={profileActionMenuItems}
                                        icon={<MoreVertOutlinedIcon fontSize="medium" />}
                                        className="h-10"
                                    />
                                )}
                                {!isCreateMode &&
                                    (exportDisabledReason ? (
                                        <KitTooltip content={exportDisabledReason}>{exportButton}</KitTooltip>
                                    ) : (
                                        exportButton
                                    ))}
                                <Button {...buttonStyle({ variant: 'outlined' })} onClick={handleCancel}>
                                    {t('cancel')}
                                </Button>
                                {isCreateMode ? draft.button : null}
                                <Tooltip text={saveDisabledReason}>
                                    <span>
                                        <PermissionButton
                                            permissions={isCreateMode ? CREATE_PERMISSIONS : UPDATE_PERMISSIONS}
                                            variant="contained"
                                            color="secondary"
                                            size="small"
                                            disabled={!canSave}
                                            onClick={() => {
                                                void handleSaveInternal();
                                            }}
                                        >
                                            {t('save')}
                                        </PermissionButton>
                                    </span>
                                </Tooltip>
                            </div>
                        )
                    }
                >
                    {isDraft || draft.detail ? (
                        <div className="flex w-full flex-wrap items-center gap-4 py-4 max-sm:gap-2">
                            <DraftChip />
                            {draftActions.draft ? <DraftMeta draft={draftActions.draft} /> : null}
                        </div>
                    ) : null}
                </PageHeader>
                {draftNotice}
                {isDraft ? (
                    <DraftActionsContext.Provider value={draftActions}>
                        <div className="max-w-[400px]">
                            <DraftSharedWith />
                        </div>
                        {draftActions.shareDialog}
                    </DraftActionsContext.Provider>
                ) : null}
                {isCreateMode ? createBody : body}
                {!isCreateMode && isLoadingProfile && <Loader />}
                {!isCreateMode && profileError && (
                    <div>
                        <p className="text-body1 text-error">{t('marketing-profiles.details.errorLoading')}</p>
                        <Button
                            {...buttonStyle({ variant: 'text' }, 'mt-2')}
                            onClick={() => navigate('/contacts/marketing')}
                        >
                            {t('marketing-profiles.details.backToProfiles')}
                        </Button>
                    </div>
                )}
            </div>
        </PaddedLayout>
    );
};

/** The new profile form loaded with what a draft holds, as a duplicate is loaded with its source. */
const MarketingProfileDraftAdd = ({ draftId }: { draftId: number }) => {
    const [initialValue, setInitialValue] = useState<MarketingProfileCreateUpdateRequest>();
    const { data: profile } = useGetMarketingProfileByIdQuery(draftId, { refetchOnMountOrArgChange: true });

    // Frozen once loaded: a refetch must not reset what the user is typing.
    if (!initialValue && profile) {
        setInitialValue({ name: profile.name ?? '', filterCriteria: profile.filterCriteria });
    }

    return initialValue ? <MarketingProfileDetailsPage initialValue={initialValue} forceCreateMode /> : <Loader />;
};

/** The profile's page; the new profile's URL with `?draft=<id>` edits that draft instead of a blank profile. */
const MarketingProfileDetailsPageOrDraft = (props: Props) => {
    const { params, location } = useCreateRoute();
    const draftId = draftIdIn(location.search);
    const isNewRoute = !params.id || params.id === 'new';
    return isNewRoute && !props.forceCreateMode && draftId !== null ? (
        <MarketingProfileDraftAdd key={draftId} draftId={draftId} />
    ) : (
        <MarketingProfileDetailsPage {...props} />
    );
};

export default MarketingProfileDetailsPageOrDraft;
